import React, { useState, useEffect, useMemo } from 'react';
import { PostCard } from '../components/social/PostCard';
import { ListingCard } from '../components/marketplace/ListingCard';
import { Link } from 'react-router-dom';
import { generateUsers, generateVendors, generateListings, generatePosts } from '../utils/mockData';
import { TrendingUp, Award, Calendar, ExternalLink } from 'lucide-react';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { CreatePost } from '../components/social/CreatePost';
import { SAMPLE_HERO, GEM_SHORTS_MOCK, avatarAt, gemAt, resolveAvatar, resolvePostMedia } from '../constants/sampleImages';

export const FeedPage = () => {
  const [realPosts, setRealPosts] = useState([]);

  useEffect(() => {
    const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRealPosts(docs);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'posts');
    });
    return () => unsubscribe();
  }, []);

  // Initialize mock data for fallback/mixing
  const data = useMemo(() => {
    const users = generateUsers(10);
    const vendors = generateVendors(users);
    const listings = generateListings(vendors, 10);
    const mockPosts = generatePosts(users, 5);
    
    const enrichPost = (post, index) => ({
      ...post,
      authorAvatar: post.authorAvatar || avatarAt(index),
      media: resolvePostMedia(post.media, index),
    });

    const enrichListing = (listing, index) => ({
      ...listing,
      images: listing.images?.length ? listing.images : [gemAt(index), gemAt(index + 3)],
    });

    // Mix items for feed
    const items = [
      ...realPosts.map((p, i) => ({ type: 'post', data: enrichPost(p, i) })),
      ...mockPosts.map((p, i) => ({ type: 'post', data: enrichPost(p, i + realPosts.length) })),
      ...listings.map((l, i) => ({ type: 'listing', data: enrichListing(l, i) })),
    ];
    
    // Sort logic: real posts first if recent, then mixed
    // For now just random mix but keep real ones prominent
    return { items, vendors: vendors.slice(0, 5) };
  }, [realPosts]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-w-0 lg:h-full lg:min-h-0 lg:overflow-hidden">
      {/* Sidebar - Left (Desktop) */}
      <div className="hidden lg:block lg:col-span-3 space-y-6 lg:shrink-0 lg:overflow-hidden">
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp size={20} className="text-primary" />
            Trending Categories
          </h2>
          <div className="space-y-2">
            {['Blue Sapphire', 'Padparadscha', 'Alexandrite', 'Star Ruby', 'Ceylon Yellow'].map(cat => (
              <button key={cat} className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg transition-colors flex items-center justify-between">
                <span>{cat}</span>
                <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded-full">New</span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-primary/5 rounded-xl border border-primary/10 p-4 shadow-sm">
          <h2 className="font-bold text-primary mb-2 flex items-center gap-2">
            <Award size={20} />
            Verified Dealers
          </h2>
          <p className="text-xs text-slate-500 mb-4">Top rated and verified businesses this week.</p>
          <div className="space-y-3">
            {data.vendors.map(vendor => (
              <div key={vendor.id} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white border border-gray-100 overflow-hidden">
                  <img src={resolveAvatar(vendor.logo)} alt={vendor.companyName} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-gray-900 truncate">{vendor.companyName}</p>
                  <p className="text-[10px] text-gray-500">Rating: {vendor.rating} ★</p>
                </div>
              </div>
            ))}
          </div>
          <button className="w-full mt-4 py-2 text-xs font-bold text-primary hover:bg-white rounded-lg transition-colors border border-transparent hover:border-primary/20">
            View Leaderboard
          </button>
        </div>
      </div>

      {/* Main Feed */}
      <div className="lg:col-span-6 space-y-6 min-w-0 lg:min-h-0 lg:overflow-y-auto lg:pr-1 custom-scrollbar">
        {/* Gem of the Day Hero */}
        <div className="relative bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm group">
          <div className="flex flex-col md:flex-row min-h-[140px] md:h-48">
            <div className="w-full md:w-1/3 bg-slate-900 relative h-32 md:h-auto">
              <div className="absolute inset-0 bg-gradient-to-tr from-secondary/20 to-transparent"></div>
              <img src={SAMPLE_HERO.gemOfTheDay} className="absolute inset-0 w-full h-full object-cover opacity-60" alt="Featured Gem" />
              <div className="h-full flex items-center justify-center relative">
                <div className="w-16 h-16 md:w-24 md:h-24 bg-white/10 rounded-full blur-xl animate-pulse"></div>
                <div className="absolute text-white text-center">
                  <span className="text-[8px] md:text-[10px] uppercase font-black tracking-widest opacity-80">DAILY GEM</span>
                  <p className="text-[10px] md:text-xs font-serif italic">Blue Sapphire</p>
                </div>
              </div>
            </div>
            <div className="w-full md:w-2/3 p-4 md:p-6 flex flex-col justify-center">
              <div className="flex justify-between items-start mb-2 md:mb-4">
                <div className="min-w-0 pr-2">
                  <h2 className="text-base md:text-xl font-black text-slate-900 tracking-tight truncate">Royal Blue Sapphire</h2>
                  <p className="text-[10px] md:text-sm text-slate-500 truncate">No Heat • 4.12 Carats</p>
                </div>
                <span className="shrink-0 bg-amber-50 text-amber-700 text-[8px] md:text-[10px] font-black px-2 py-1 rounded tracking-wider border border-amber-100">PLATINUM</span>
              </div>
              <div className="mt-2 md:mt-auto flex justify-between items-center gap-2">
                <div>
                  <p className="text-[8px] md:text-[10px] text-slate-400 font-bold uppercase tracking-widest">Valuation</p>
                  <p className="text-lg md:text-2xl font-black text-accent tracking-tighter">Rs. 5.4M</p>
                </div>
                <Link to="/marketplace" className="bg-primary text-white px-4 md:px-6 py-2 rounded-full font-black text-[10px] md:text-sm hover:scale-105 transition-all shadow-lg shadow-primary/20">Market</Link>
              </div>
            </div>
          </div>
        </div>

        <CreatePost />

        {/* Gem Shorts */}
        <div className="space-y-4 min-w-0">
          <div className="flex items-center justify-between px-2">
            <h2 className="font-black text-slate-900 text-sm uppercase tracking-widest flex items-center gap-2">
              <TrendingUp size={16} className="text-primary" />
              Gem Shorts
            </h2>
            <button className="text-[10px] font-bold text-primary uppercase tracking-tighter hover:underline">Watch All</button>
          </div>
          <div className="flex w-full max-w-full gap-4 overflow-x-auto overscroll-x-contain pb-4 scroll-smooth snap-x snap-mandatory touch-pan-x no-scrollbar">
            {GEM_SHORTS_MOCK.map((short, i) => (
              <div key={short.id} className="shrink-0 snap-start w-32 md:w-40 aspect-[9/16] rounded-3xl bg-slate-900 overflow-hidden relative group cursor-pointer shadow-lg shadow-slate-200">
                <img
                  src={short.thumbnail}
                  alt={short.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-0 transition-opacity duration-500"
                />
                <video
                  src={short.video}
                  poster={short.thumbnail}
                  className="w-full h-full object-cover opacity-80 group-hover:scale-110 group-hover:opacity-100 transition-all duration-700"
                  muted
                  loop
                  playsInline
                  preload="none"
                  onMouseEnter={(e) => {
                    e.currentTarget.play().catch(() => {});
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.pause();
                    e.currentTarget.currentTime = 0;
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none"></div>
                <div className="absolute bottom-3 left-3 right-3 text-white pointer-events-none">
                   <div className="flex items-center gap-1.5 mb-1">
                      <div className="w-4 h-4 rounded-full bg-primary border border-white/50 overflow-hidden">
                        <img src={resolveAvatar(null, i)} alt={short.dealerName} className="w-full h-full object-cover" />
                      </div>
                      <span className="text-[8px] font-bold truncate">{short.dealerName}</span>
                   </div>
                  <p className="text-[8px] md:text-[10px] font-medium line-clamp-2 leading-tight">{short.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {data.items.map((item, idx) => (
          <div key={idx}>
            {item.type === 'post' ? (
              <PostCard post={item.data} imageIndex={idx} />
            ) : (
              <div className="mb-4">
                <div className="flex items-center justify-between px-2 mb-2">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">New Listing in Marketplace</span>
                  <button className="text-xs text-primary font-bold">See More</button>
                </div>
                <ListingCard listing={item.data} variant="feed" />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Sidebar - Right (Desktop) */}
      <div className="hidden lg:block lg:col-span-3 space-y-6 lg:shrink-0 lg:overflow-hidden">
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Calendar size={20} className="text-warning" />
            Upcoming Events
          </h2>
          <div className="space-y-4">
            {[
              { title: 'Colombo Gem Expo 2026', date: 'Jul 15', type: 'Exhibitions' },
              { title: 'Beruwala Rough Trade Meet', date: 'Jun 28', type: 'Trade Shows' }
            ].map(event => (
              <div key={event.title} className="group cursor-pointer">
                <div className="flex gap-3">
                  <div className="bg-slate-50 w-12 h-12 rounded-lg flex flex-col items-center justify-center border border-slate-100 group-hover:bg-primary/5 group-hover:border-primary/10 transition-colors">
                    <span className="text-[10px] font-bold text-primary">{event.date.split(' ')[0]}</span>
                    <span className="text-sm font-bold text-slate-900">{event.date.split(' ')[1]}</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900 line-clamp-1 group-hover:text-primary transition-colors">{event.title}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">{event.type}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button className="w-full mt-4 py-2 text-xs font-bold text-gray-500 hover:text-gray-900 border border-gray-100 rounded-lg hover:bg-gray-50 transition-all">
            Browse All Events
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm overflow-hidden relative group">
          <img 
            src={SAMPLE_HERO.industryNews} 
            className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:scale-110 transition-transform duration-700" 
            alt="Promotion"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
          <div className="relative z-10 pt-20">
            <span className="bg-white/20 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded uppercase mb-2 inline-block">Industry News</span>
            <h3 className="text-white font-bold leading-tight mb-2">Export Regulation Changes for 2026 Announced</h3>
            <button className="flex items-center gap-1 text-white text-xs font-bold hover:underline">
              Read Article <ExternalLink size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
